import { API_BASE_URL } from '@/config/api';
import { apiClient } from '@/lib/api/client';
import { withOrganizationHeader, getCurrentOrganizationId } from '@/lib/api/headers';

export interface OrganizationMetadata {
  description?: string;
  website?: string;
  logoUrl?: string;
  features?: string;
  allowedEmailDomains?: string;
}

export interface OrganizationDetails {
  id: string;
  name: string;
  slug: string;
  metadata: OrganizationMetadata;
  created_at: string;
  updated_at: string;
}

export interface OrganizationUpdateRequest {
  name: string;
  metadata: OrganizationMetadata;
}

export interface OrganizationMember {
  id: string;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  role: {
    name: string;
    slug: string;
  };
  joinedAt: string;
  lastActive: string;
  status: string;
  userId?: string;
}

export interface OrganizationMembersResponse {
  members: OrganizationMember[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface OrganizationMembersParams {
  page?: number;
  limit?: number;
  query?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateOrganizationRequest {
  name: string;
  metadata?: OrganizationMetadata;
}

export interface ListOrganizationsResponse {
  organizations: OrganizationDetails[];
  total: number;
}

export interface ListOrganizationsParams {
  page?: number;
  limit?: number;
  query?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export async function listOrganizations(params?: ListOrganizationsParams) {
  if (import.meta.env.DEV) {
    console.log('[API:Organizations] Listing organizations with params:', params);
  }
  
  try {
    const endpoint = '/api/organizations';
    const result = await apiClient.get<ListOrganizationsResponse>(endpoint, { 
      params: params as Record<string, string> 
    });
    
    if (!result.success) {
      if (import.meta.env.DEV) {
        console.error('[API:Organizations] Failed to list organizations:', result.error);
      }
    }
    
    return result;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[API:Organizations] Error in listOrganizations:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error in listOrganizations',
      data: null
    };
  }
}

export async function getOrganizationDetails(organizationId: string) {
  if (import.meta.env.DEV) {
    console.log('[API:Organizations] Getting details for organization ID:', organizationId);
  }
  
  try {
    const endpoint = `/api/organizations/${organizationId}`;
    const config = withOrganizationHeader(organizationId);
    const result = await apiClient.get<OrganizationDetails>(endpoint, config);
    
    if (!result.success) {
      if (import.meta.env.DEV) {
        console.error('[API:Organizations] Failed to fetch organization details:', result.error);
      }
    }
    
    return result;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[API:Organizations] Error in getOrganizationDetails:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error in getOrganizationDetails',
      data: null
    };
  }
}

export async function updateOrganizationDetails(organizationId: string, data: OrganizationUpdateRequest) {
  const config = withOrganizationHeader(organizationId);
  return apiClient.put<OrganizationDetails>(`/api/organizations/${organizationId}`, data, config);
}

export async function getOrganizationMembers(organizationId: string, params?: OrganizationMembersParams) {
  const config = withOrganizationHeader(organizationId, { params: params as Record<string, string> });
  return apiClient.get<OrganizationMembersResponse>(`/api/organizations/${organizationId}/members`, config);
}

export async function createOrganization(data: CreateOrganizationRequest) {
  if (import.meta.env.DEV) {
    console.log('[API:Organizations] Creating new organization:', data.name);
  }
  
  try {
    const endpoint = '/api/organizations';
    const result = await apiClient.post<OrganizationDetails>(endpoint, data);
    
    if (!result.success) {
      if (import.meta.env.DEV) {
        console.error('[API:Organizations] Failed to create organization:', result.error);
      }
    }
    
    return result;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[API:Organizations] Error in createOrganization:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error in createOrganization',
      data: null
    };
  }
} 